// ============================================================================
// MessagesPopup (Iter43)
// ---------------------------------------------------------------------------
// Modal that shows the ASA group conversations inline, on top of any page
// (project view, task drawer, etc). When user clicks X, the popup closes and
// the user stays exactly where they were — no navigation, no state loss.
// ============================================================================
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { X, Send, Loader2 } from './icons';

export default function MessagesPopup({ onClose }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/conversations').then((r) => {
      setConvs(r.data || []);
      if ((r.data || []).length && !active) setActive(r.data[0]);
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/conversations/${active.id}/messages`)
      .then((r) => setMessages(r.data || []))
      .catch(() => {});
  }, [active]);

  const send = async () => {
    if (!input.trim() || !active) return;
    setSending(true);
    try {
      await api.post('/conversations/message', { conversation_id: active.id, content: input.trim() });
      setInput('');
      const r = await api.get(`/conversations/${active.id}/messages`);
      setMessages(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="messages-popup"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6,11,24,0.72)',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 960, height: '80vh',
          background: 'var(--sf)', borderRadius: 18,
          border: '1.5px solid var(--bd)',
          display: 'grid', gridTemplateColumns: '260px 1fr',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar */}
        <div style={{ borderRight: '1.5px solid var(--bd)', overflowY: 'auto', background: 'var(--sf2)' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1.5px solid var(--bd)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="asa-eyebrow" style={{ flex: 1 }}>Conversations</div>
            <button
              data-testid="messages-popup-close"
              onClick={onClose}
              style={{ display: 'grid', placeItems: 'center', cursor: 'pointer' }}
            >
              <X size={16} color="var(--t3)" />
            </button>
          </div>
          {loading ? (
            <div style={{ padding: 16, color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12 }}>Chargement…</div>
          ) : convs.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12 }}>Aucune conversation.</div>
          ) : (
            convs.map((c) => (
              <button
                key={c.id}
                data-testid={`popup-conv-${c.id}`}
                type="button"
                onClick={() => setActive(c)}
                style={{
                  width: '100%', textAlign: 'left', padding: '12px 16px',
                  background: active?.id === c.id ? 'var(--sf)' : 'transparent',
                  borderBottom: '1px solid var(--bd)',
                  cursor: 'pointer', color: 'var(--t1)',
                  fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
                }}
              >
                {c.name || c.title || 'Conversation'}
                <div style={{ fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)', marginTop: 3 }}>
                  {c.type === 'group' ? '👥 Groupe' : '💬 DM'}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Thread */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{
            padding: '14px 18px', borderBottom: '1.5px solid var(--bd)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)' }}>
              {active ? (active.name || active.title || 'Conversation') : 'Sélectionne une conversation'}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.length === 0 ? (
              <div style={{ color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12, textAlign: 'center', marginTop: 40 }}>
                Aucun message. Envoie le premier ↓
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} style={{
                  padding: '8px 12px', borderRadius: 10,
                  background: 'var(--sf2)', border: '1px solid var(--bd)',
                  fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                }}>
                  <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: 'var(--t3)', marginBottom: 2 }}>
                    {m.author_name || m.sender_id || '—'}
                  </div>
                  {m.content}
                </div>
              ))
            )}
          </div>
          {active && (
            <div style={{
              padding: '12px 18px', borderTop: '1.5px solid var(--bd)',
              display: 'flex', gap: 8, background: 'var(--sf2)',
            }}>
              <input
                data-testid="messages-popup-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Écris un message…"
                style={{
                  flex: 1, padding: '10px 12px',
                  background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 10,
                  fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
                }}
              />
              <button
                data-testid="messages-popup-send"
                type="button"
                onClick={send}
                disabled={sending || !input.trim()}
                className="asa-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {sending ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
                Envoyer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
